import React, { useCallback, useMemo } from 'react';

import type { ChipProps } from '@mui/material';
import { Autocomplete, Box, Checkbox, Chip, Typography } from '@mui/material';
import { AlineAutocompleteInput } from 'src/components/AlineAutocompleteInput';
import { MSExcelIcon } from 'src/components/icons/MSExcelIcon';
import { MSPPTIcon } from 'src/components/icons/MSPPTIcon';
import { MSWordIcon } from 'src/components/icons/MSWordIcon';
import { PDFIcon } from 'src/components/icons/PDFIcon';
import { makeStyles } from 'tss-react/mui';

import type { FileTypeLabel } from './file-type-presets';
import { CUSTOM_FILE_TYPES, getMimeTypeLabels, labelsToMimeTypes } from './file-type-presets';

const OPTIONS: FileTypeLabel[] = CUSTOM_FILE_TYPES.map(ft => ft.label);

const useStyles = makeStyles()(theme => ({
  root: {
    minWidth: 240,
  },
  tag: {
    maxWidth: 'unset',
  },
  tagLabel: {
    padding: theme.spacing(0, 0.5),
  },
}));

export const FileTypeSelector = React.memo(function FileTypeSelector({
  onChange,
  value,
}: {
  onChange: (mimeTypes: string[]) => void;
  value: string[];
}) {
  const { classes } = useStyles();
  const selectedLabels = useMemo(() => getMimeTypeLabels(value), [value]);

  const handleChange = useCallback(
    (_: unknown, newLabels: FileTypeLabel[]) => onChange(labelsToMimeTypes(newLabels)),
    [onChange]
  );

  return (
    <Autocomplete
      disableCloseOnSelect
      multiple
      className={classes.root}
      getOptionLabel={option => option}
      options={OPTIONS}
      renderInput={params => (
        <AlineAutocompleteInput {...params} placeholder="Select file types..." />
      )}
      renderOption={({ key, ...props }, option, { selected }) => (
        <li key={key} {...props}>
          <Box sx={theme => ({ alignItems: 'center', display: 'flex', gap: theme.spacing(1) })}>
            <Checkbox checked={selected} size="small" sx={{ p: 0 }} />
            <FileTypeChipIcon height={20} label={option} width={20} />
            <Typography variant="body2">{option}</Typography>
          </Box>
        </li>
      )}
      renderValue={(selected, getItemProps) =>
        selected.map((label, index) => {
          const { key, ...tagProps } = getItemProps({ index });
          return <FileTypeFilterChip key={key} {...tagProps} label={label} />;
        })
      }
      value={selectedLabels}
      onChange={handleChange}
    />
  );
});

interface FileTypeFilterChipProps extends ChipProps {
  label: FileTypeLabel;
}

export function FileTypeFilterChip({ label, ...props }: FileTypeFilterChipProps): JSX.Element {
  return (
    <Chip
      {...props}
      icon={<FileTypeChipIcon label={label} />}
      label={label}
      size="small"
      variant="outlined"
    />
  );
}

export function FileTypeChipIcon({
  className,
  height = 16,
  label,
  width = 16,
}: {
  label: FileTypeLabel;
  className?: string;
  height?: number;
  width?: number;
}): JSX.Element {
  switch (label) {
    case 'PDF':
      return <PDFIcon className={className} height={height} width={width} />;
    case 'MS Word':
      return <MSWordIcon className={className} height={height} width={width} />;
    case 'MS Excel':
      return <MSExcelIcon className={className} height={height} width={width} />;
    case 'MS PowerPoint':
      return <MSPPTIcon className={className} height={height} width={width} />;
  }
}
